import { useIntl } from "react-intl";

import { type SingleFightDetailsData } from "@/features/flights/contexts/SingleFlightDetailsContext";
import { extractAirlineCarriers } from "@/features/flights/utils/extractAirlineCarriers";
import { parseDuration } from "@/utils/duration";

import { AirlineAvatarGroup } from "../AirlineAvatarGroup";
import { BaggageAllowance } from "../BaggageAllowance";
import { FlightInfo, FlightOverallTime } from "../flights/FlightInfo";

function FlightSummary({
  selectedFlight,
}: {
  selectedFlight?: SingleFightDetailsData;
}) {
  const intl = useIntl();
  const departureFlight = selectedFlight?.flights?.[0] || null;
  const departureSegments = departureFlight?.segments || [];
  const departureStops = departureFlight?.stops || [];

  const returnFlight =
    ((selectedFlight?.flights?.length || 0) > 1 &&
      selectedFlight?.flights?.[selectedFlight?.flights.length - 1]) ||
    null;
  const returnSegments = returnFlight?.segments || [];
  const returnStops = returnFlight?.stops || [];

  const departureAirlines = extractAirlineCarriers(departureSegments) || [];
  const returnAirlines = extractAirlineCarriers(returnSegments) || [];

  return (
    <div className="flex w-full flex-col">
      <div className="flex w-full">
        <span className="text-base font-medium text-text-title">
          {intl.formatMessage({
            defaultMessage: "Flight summary",
            description: "Section heading for the flight itinerary summary",
          })}
        </span>
      </div>
      <span className="section-title mt-6">
        {intl.formatMessage({
          defaultMessage: "Flight details",
          description: "Section heading for detailed flight information",
        })}
      </span>
      <div className="section-card mb-2 flex flex-col gap-4">
        <div className="flex w-full items-center gap-2">
          <div className="flex w-fit">
            <AirlineAvatarGroup airlines={departureAirlines} />
          </div>
          <div>
            {departureAirlines?.length &&
              [...departureAirlines]
                ?.splice(0, 2)
                .map((airline, index, array) => {
                  const doesOperatingCarrierDiffer =
                    airline.code !== airline.operatingCarrier.code;

                  return (
                    <span
                      key={airline.name || index}
                      className="text-sm text-text-primary"
                    >
                      {airline.name ||
                        intl.formatMessage({
                          defaultMessage: "Unknown airline",
                          description:
                            "Fallback label displayed when the airline name is unavailable",
                        })}
                      {doesOperatingCarrierDiffer ? (
                        <>
                          {" "}
                          <span className="text-text-tertiary">
                            (
                            {intl.formatMessage({
                              defaultMessage: "Flight operated by",
                              description:
                                "Prefix text before the operating carrier name (e.g. 'Flight operated by Fly Baghdad')",
                            })}{" "}
                            {airline.operatingCarrier.name ||
                              airline.operatingCarrier.code}
                            )
                          </span>
                        </>
                      ) : null}
                      {array.length > index + 1 ? ", " : ""}
                    </span>
                  );
                })}
            {departureAirlines?.length - 2 > 0 && (
              <span className="mr-1 text-sm text-text-primary">
                +{departureAirlines.length - 2}
              </span>
            )}
          </div>
        </div>

        <FlightInfo
          origin={{
            date: departureSegments[0].departure.date,
            time: departureSegments[0].departure.time,
            code: departureSegments[0].departure.airport.code,
          }}
          destination={{
            date: departureSegments[departureSegments.length - 1].arrival.date,
            time: departureSegments[departureSegments.length - 1].arrival.time,
            code: departureSegments[departureSegments.length - 1].arrival
              .airport.code,
          }}
        >
          <FlightOverallTime
            overallTime={intl.formatMessage(
              {
                defaultMessage: "{hours}h {minutes}m",
                description:
                  "Formatted flight or travel duration (e.g. '2h 30m')",
              },
              departureFlight?.duration
                ? parseDuration(departureFlight.duration)
                : undefined,
            )}
            stops={departureStops?.length}
          />
        </FlightInfo>
      </div>
      <BaggageAllowance baggageData={selectedFlight?.outboundBaggage} />

      {/* Return flight if its round trip */}
      {returnFlight && (
        <>
          <div className="section-card mt-4 mb-2 flex flex-col gap-4">
            <div className="flex w-full items-center gap-2">
              <div className="flex w-fit">
                <AirlineAvatarGroup airlines={returnAirlines} />
              </div>
              <div>
                {returnAirlines?.length &&
                  [...returnAirlines]
                    ?.splice(0, 2)
                    .map((airline, index, array) => {
                      const doesOperatingCarrierDiffer =
                        airline.code !== airline.operatingCarrier.code;

                      return (
                        <span
                          key={airline.name || index}
                          className="text-sm text-text-primary"
                        >
                          {airline.name ||
                            intl.formatMessage({
                              defaultMessage: "Unknown airline",
                              description:
                                "Fallback label displayed when the airline name is unavailable",
                            })}
                          {doesOperatingCarrierDiffer ? (
                            <>
                              {" "}
                              <span className="text-text-tertiary">
                                (
                                {intl.formatMessage({
                                  defaultMessage: "Flight operated by",
                                  description:
                                    "Prefix text before the operating carrier name (e.g. 'Flight operated by Fly Baghdad')",
                                })}{" "}
                                {airline.operatingCarrier.name ||
                                  airline.operatingCarrier.code}
                                )
                              </span>
                            </>
                          ) : null}
                          {array.length > index + 1 ? ", " : ""}
                        </span>
                      );
                    })}

                {returnAirlines?.length - 2 > 0 && (
                  <span className="mr-1 text-sm text-text-primary">
                    +{returnAirlines.length - 2}
                  </span>
                )}
              </div>
            </div>

            <FlightInfo
              origin={{
                date: returnSegments[0].departure.date,
                time: returnSegments[0].departure.time,
                code: returnSegments[0].departure.airport.code,
              }}
              destination={{
                date: returnSegments[returnSegments.length - 1].arrival.date,
                time: returnSegments[returnSegments.length - 1].arrival.time,
                code: returnSegments[returnSegments.length - 1].arrival.airport
                  .code,
              }}
            >
              <FlightOverallTime
                overallTime={intl.formatMessage(
                  {
                    defaultMessage: "{hours}h {minutes}m",
                    description:
                      "Formatted flight or travel duration (e.g. '2h 30m')",
                  },
                  returnFlight?.duration
                    ? parseDuration(returnFlight.duration)
                    : undefined,
                )}
                stops={returnStops.length}
              />
            </FlightInfo>
          </div>
          {selectedFlight?.returnBaggage !== null ? (
            <BaggageAllowance baggageData={selectedFlight?.returnBaggage} />
          ) : null}
        </>
      )}
    </div>
  );
}

export default FlightSummary;
